import React, { useEffect, useState } from 'react';
import { Button, FormGroup, Tooltip, withStyles, createTheme, ThemeProvider, SvgIcon } from '@material-ui/core';
import { Form, Col, Row } from 'react-bootstrap';
import { object, string, date } from '../../../Helpers/Validators/customValidators';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import { sub } from 'date-fns';
import States from '../../../Helpers/Lists/States';
import StatesWithDisabled from '../../../Helpers/Lists/StatesWithDisabled';
import { InputMaskDate } from '../../../Helpers/Formatters';
import { TextInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs';
import userIcon from '../../../../icons/user.svg';
import {
  getBasicInfo,
  setBasicInfo,
} from '../../../../sessionStorage';
import './AboutMe.scss';
import InfoIcon from '@material-ui/icons/Info';
import ShieldCheckmarkIcon from '../../../../icons/shield-checkmark.svg';
import { useHistory } from 'react-router-dom';
import MaleIcon from '../../../../icons/male.svg';
import FemaleIcon from '../../../../icons/female.svg';

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: 14,
    boxShadow: theme.shadows[1],
  },
}))(Tooltip);

export default function WholeLifeAboutMeForm(props) {
  const history = useHistory();
  const theme = createTheme({
    palette: {
      primary: {
        main: `${props.brandTheme ? props.brandTheme : "#3f51b5"}`,
      },
    },
    typography: {
      fontFamily: "museo-sans",
      body1: {
        fontSize: 14,
        fontWeight: 500,
      },
    },
    overrides: {
      MuiInputBase: {
        input: {
          fontSize: 16,
        },
        root: {
          height: 56,
        },
      },
      MuiFilledInput: {
        root: {
          backgroundColor: "rgba(0, 0, 0, 0.05)",
        },
        underline: {
          "&::before": {
            borderBottom: "solid 2px #b2b2b2",
          },
          "&:hover::before": {
            borderBottomWidth: "2px",
          },
        },
      },
      MuiFormLabel: {
        root: {
          fontSize: 16,
          color: "rgba(0,0,0,0.6)",
        },
      },
      MuiFormHelperText: {
        root: {
          marginTop: 0,
          paddingTop: 0,
        },
      },
      MuiInputLabel: {
        shrink: {
          color: "rgba(0,0,0,0.3) !important",
        },
      },
      MuiSelect: {
        fontSize: 16,
        icon: {
          color: "rgba(0,0,0,1)",
        },
        iconFilled: {
          right: 16,
        },
      },
    },
  });

  const basicInfo = getBasicInfo();
  const [formState, setFormState] = useState(WholeLifeAboutMeForm.DefaultFormState(false, basicInfo));

  const [tooltip, setTooltip] = useState(false);
  const handleTooltipAboutClose = () => {
    setTooltip(false);
  };
  const handleTooltipAboutOpen = () => {
    setTooltip(true);
  };

  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: WholeLifeAboutMeForm.ValidationSchema(props.ageSpecs),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const handleValueChange = (field, value) => {
    setValue(field, value);
    setFormState({ ...formState, [field]: value });
    if (field === 'gender') {
      triggerValidation(field);
    }
  };

  useEffect(() => {
    // manually register fields for validation that don't play nice with react-hook-form register
    register({ name: 'state' });
    setValue('state', formState.state);
    register({ name: 'gender' });
    setValue('gender', formState.gender);
    register({ name: 'birthdate' });
    setValue('birthdate', formState.birthdate);
    register({ name: 'email' });
    setValue('email', formState.email);
    register({ name: 'usResident' });
    setValue('usResident', formState.usResident);
    register({ name: 'bmi' });
    setValue('bmi', formState.bmi);
  }, [register]);

  const onSubmit = () => {
      // Save formState to basicinfo
      const basicInfoFromSessionStorage = getBasicInfo();
      basicInfoFromSessionStorage.gender = formState.gender;
      basicInfoFromSessionStorage.birthdate = formState.birthdate;
      basicInfoFromSessionStorage.state = formState.state;
      basicInfoFromSessionStorage.email = formState.email;
      basicInfoFromSessionStorage.usResident = formState.usResident;

      setBasicInfo(basicInfoFromSessionStorage);

      if (props.openEditPopUp) {
        history.push('/term-insurance/quote-results')
        props.setOpenEditPopUp(false)
      } else {
        props.onNext();
      }
  }

  return (
    <ThemeProvider theme={theme}>
      <div className={`basic-info-about-me-whole-life`}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col lg={12}>
              {props.showHeader && (
                <React.Fragment>
                  <Row className="justify-content-center">
                    <SvgIcon color='primary' component={userIcon} className="icons" fontSize="large"/>
                  </Row>
                  <Row className="justify-content-center">
                    <h3 className="py-3">Profile</h3>
                    <Tooltip
                      title="This information should represent the person who will be insured and covered by the policy, whether that's you or someone else."
                      id="about-tooltip" aria-describedby="about-tooltip" aria-label="more about sort by price"
                      className="toolInfo" role="tooltip">
                      <LightTooltip enterTouchDelay={0} leaveTouchDelay={5000}
                                    onClose={() => handleTooltipAboutClose()}
                                    open={tooltip}>
                        <button type='button' id="about" role="tooltip" className="quote-tooltip"
                                onMouseOver={() => handleTooltipAboutOpen()} onClick={() => handleTooltipAboutOpen()}
                                tabIndex="0" aria-disabled="true" aria-describedby="about"><InfoIcon
                          color="action" /></button>
                      </LightTooltip>
                    </Tooltip>
                  </Row>
                </React.Fragment>
              )}

              <Row>
                <Col>
                  <FormGroup>
                    <StyledRadioInput
                      name="gender"
                      id="gender"
                      label=""
                      value={formState.gender}
                      options={[
                        { value: "F", label: "Female", icon: <FemaleIcon /> }, 
                        { value: "M", label: "Male", icon: <MaleIcon /> },
                      ]}
                      onChange={e => handleValueChange('gender', e)}
                      errors={errors.gender}
                      isBlankIcon={true}
                      isBlankCheckMark={false}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className='mt-2'>
                <Col>
                  <Form.Group>
                    <TextInput
                      name="birthdate"
                      id="birthdate"
                      label="Date of Birth"
                      value={formState.birthdate}
                      errors={errors && errors.birthdate}
                      inputRef={register}
                      onChange={(e) => handleValueChange('birthdate', e.target.value)}
                      InputProps={{
                        inputComponent: InputMaskDate,
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className='mt-2'>
                <Col>
                  <Form.Group>
                    <SelectInput
                      name="state"
                      id="state"
                      value={formState.state}
                      onChange={(e) => handleValueChange('state', e.target.value)}
                      label="State"
                      inputRef={register}
                      selectOptions={props.unavailableStates ? StatesWithDisabled(props.unavailableStates) : States()}
                      errors={errors && errors.state}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Col>
            {props.showQuoteButton &&
              <Col xs={12}>
                <Button variant="contained" color="primary" type="submit" fullWidth className='submit-button'>
                  <ShieldCheckmarkIcon style={{ width: "20px" }} className='mr-1'/>UPDATE MY QUOTE</Button>
              </Col>
            }
          </Row>
          {props.quickQuoteDisclaimer && props.submitButtonVariant === 'landing' &&
            <Row>
              <Col className="quick-quote-disclaimer-container"
                   dangerouslySetInnerHTML={{ __html: props.quickQuoteDisclaimer }} />
            </Row>
          }
          <Row className="mt-0">
            {!props.submitButtonVariant && !props.hideNextButton &&
              <Col className="text-right pb-3">
                <Button variant="contained" size="medium" color="primary" type="submit">
                  NEXT
                </Button>
              </Col>
            }
            {props.submitButtonVariant && props.submitButtonVariant === 'landing' &&
              <Col className="d-flex mt-3">
                <Button variant="contained" color="primary" type="submit" fullWidth className='submit-button'>
                  <ShieldCheckmarkIcon className="button-icon"/>Continue</Button>
              </Col>
            }
          </Row>
        </Form>
      </div>
    </ThemeProvider>
  )
}

WholeLifeAboutMeForm.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required('Gender is required.'),
    birthdate: date()
      .typeError('Invalid date.')
      .max(sub(new Date(), {
        years: ageSpecs.min,
        days: ageSpecs.minAgeDays || 0,
      }), `Minimum age is ${ageSpecs.minAgeDays ? `${ageSpecs.minAgeDays } days` : ageSpecs.min}.`)
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required('Birthdate is required.'),
    state: string().required('State is required.'),
    email: string().notRequired().email('Invalid email.').nullable(),
  })
};

WholeLifeAboutMeForm.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1980' : (basicInfo && basicInfo.birthdate) || '',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    email: prefill ? 'mailto:user@address.com' : (basicInfo && basicInfo.email) || '',
    usResident: true,
    bmi: (basicInfo && basicInfo.bmi) || undefined,
  };
};



// WEBPACK FOOTER //
// ./src/components/Quoters/WholeLife/steps/AboutMeForm.js